import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { rules, axiosConfig } from 'api';
import { AxiosResponse } from 'axios';
import { RootState } from 'store';

import useSelectionOrigin from 'hooks/useSelectionOrigin';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { GroupedListModel } from 'models/groupedList';
import { RulesResponse } from 'models/rule';
import GroupSelectRule from 'screens/components/RuleSelection';

interface Props {
  groupTarget: string;
  ruleSelected: GroupedListModel;
  showRulesSelection?: boolean;
  onConfirmSelection: (rule: GroupedListModel) => void;
  onCloseSelectionScreen: () => void;
}

const RuleSelectionContainer = ({
  groupTarget,
  onConfirmSelection,
  onCloseSelectionScreen,
  ruleSelected,
  showRulesSelection,
}: Props) => {
  const [ruleList, setRuleList] = useState<GroupedListModel[]>([]);
  const [isLoading, setLoading] = useState(false);
  const toast = useToast();
  const { t } = useStoreTranslation();
  const companyId = useSelector((state: RootState) => state.user.accountResult?.companyId);
  const { itemCreated } = useSelectionOrigin();

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await rules.get<AxiosResponse<RulesResponse[]>>('/rules/getall', axiosConfig);
        setRuleList(
          data.map((rule: RulesResponse) => ({
            id: rule.id,
            name: rule.name,
            badges: rule.groups.map((badge) => ({ id: badge.userGroupId, name: badge.userGroupName })),
          })),
        );
      } catch (error) {
        toast.add({
          type: 'danger',
          message: t('group.groupAggregation.errorFetch'),
        });
        setLoading(false);
      }
      setLoading(false);
    };
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, itemCreated]);

  return (
    <GroupSelectRule
      showRulesSelection={showRulesSelection}
      groupTarget={groupTarget}
      ruleSelected={ruleSelected}
      ruleList={ruleList}
      isLoading={isLoading}
      onCloseSelectionScreen={onCloseSelectionScreen}
      onConfirmSelection={onConfirmSelection}
    />
  );
};

export default RuleSelectionContainer;
